<template>
  <div>
    <p class="title mb-1" style="margin-top: 25px">Approve Funds</p>
    <p class="has-text-grey" style="margin-bottom: 20px">Manage CMTY token allowance for the Orchestrator contract</p>

    <div class="box m-5">
      <div class="columns">
        <div class="column">

          <!-- Treasury Info -->
          <div class="my-4">
            <p class="has-text-grey is-size-7">Treasury Address</p>
            <p class="is-size-6 has-text-weight-bold">{{ treasuryAddress }}</p>
          </div>

          <hr />

          <!-- Balances -->
          <div class="columns my-4">
            <div class="column">
              <div class="notification is-light">
                <p class="has-text-grey is-size-7">Treasury CMTY Balance</p>
                <p class="is-size-4 has-text-weight-bold">{{ treasuryBalance }} CMTY</p>
              </div>
            </div>
            <div class="column">
              <div class="notification is-light">
                <p class="has-text-grey is-size-7">Current Allowance to Orchestrator</p>
                <p class="is-size-4 has-text-weight-bold">{{ currentAllowance }} CMTY</p>
              </div>
            </div>
          </div>

          <hr />

          <!-- Approve Section -->
          <div class="my-4">
            <p class="has-text-grey is-size-7 mb-2">Approve CMTY to Orchestrator</p>
            <div class="field has-addons mb-1">
              <p class="control is-expanded">
                <input
                  v-model="approveAmount"
                  class="input"
                  type="number"
                  step="any"
                  min="0"
                  :max="treasuryBalance"
                  placeholder="Amount of CMTY to approve"
                />
              </p>
              <p class="control">
                <button @click="setMaxAmount()" class="button is-info">Max</button>
              </p>
              <p class="control">
                <button @click="approveFunds()" class="button is-dark" :disabled="(approveAmount === '' || approveAmount === null) || isApproving">
                  {{ isApproving ? 'Approving...' : 'Approve' }}
                </button>
              </p>
            </div>
            <p class="help has-text-grey">Enter the amount in CMTY (not in wei). Must be equal or less than your balance.</p>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as util from "@/utilities/util.js";
import Web3 from "web3";

export default {
  name: "ApproveFundsPage",
  data() {
    return {
      account: null,
      treasuryAddress: "",
      treasuryBalance: "0",
      currentAllowance: "0",
      approveAmount: "",
      isApproving: false,
      tokenContract: null,
      orchestratorContract: null,
      decimals: 18,
    };
  },
  async mounted() {
    this.$watch(
      () => this.$store.state.isConnected,
      (val) => { this.isConnected = val; }
    );

    await util.reconnect();
    this.$store.commit("changeConnectionStatus", true);
    this.account = await util.getCoinbase();
    this.$store.commit("changeAccount", this.account);

    // Initialize contracts
    try {
      if (!window.myWeb3) {
        await util.reconnect();
      }

      this.tokenContract = new window.myWeb3.eth.Contract(
        util.CMTY_ABI,
        util.CMTY_TokenContractAddress
      );

      this.orchestratorContract = new window.myWeb3.eth.Contract(
        util.ORCHESTRATOR_ABI,
        util.ORCHESTRATOR_ContractAddress
      );

      // Get decimals
      this.decimals = Number(await this.tokenContract.methods.decimals().call());

    } catch (error) {
      console.error("Error initializing contracts:", error);
      util.toast.fire({ icon: "error", title: "Error connecting to blockchain" });
      return;
    }

    // Check if user is the treasury
    try {
      this.treasuryAddress = await this.orchestratorContract.methods.treasury().call();

      if (this.account.toLowerCase() !== this.treasuryAddress.toLowerCase()) {
        util.toast.fire({
          icon: "error",
          title: "Only the treasury account can access this page",
        });
        this.$router.push("/");
        return;
      }
    } catch (error) {
      console.error("Error checking treasury:", error);
      util.toast.fire({ icon: "error", title: "Error verifying treasury account" });
      this.$router.push("/");
      return;
    }

    // Load balances
    await this.loadBalances();
  },
  methods: {
    formatFromWei(value) {
      try {
        return Web3.utils.fromWei(String(value), "ether");
      } catch {
        return "0";
      }
    },

    async loadBalances() {
      try {
        // Treasury CMTY balance
        const balanceWei = await this.tokenContract.methods.balanceOf(this.treasuryAddress).call();
        this.treasuryBalance = this.formatFromWei(balanceWei);

        // Current allowance from treasury to Orchestrator
        const allowanceWei = await this.tokenContract.methods
          .allowance(this.treasuryAddress, util.ORCHESTRATOR_ContractAddress)
          .call();
        this.currentAllowance = this.formatFromWei(allowanceWei);
      } catch (error) {
        console.error("Error loading balances:", error);
        util.toast.fire({ icon: "error", title: "Error loading balances" });
      }
    },

    setMaxAmount() {
      this.approveAmount = this.treasuryBalance;
    },

    async approveFunds() {
      if (this.approveAmount === "" || this.approveAmount === null || Number(this.approveAmount) < 0) {
        util.toast.fire({ icon: "error", title: "Please enter a valid amount" });
        return;
      }

      if (Number(this.approveAmount) > Number(this.treasuryBalance)) {
        util.toast.fire({ icon: "error", title: "Amount exceeds your balance" });
        return;
      }

      this.isApproving = true;
      let loader = this.$loading.show({});

      try {
        const amountWei = Web3.utils.toWei(String(this.approveAmount), "ether");

        const estimatedGas = Number(await this.tokenContract.methods
          .approve(util.ORCHESTRATOR_ContractAddress, amountWei)
          .estimateGas({ from: this.account }));
        const gasLimit = Math.floor(estimatedGas * util.GAS_MULTIPLIER);
        console.log('Estimated gas:', estimatedGas, '| Gas multiplier:', util.GAS_MULTIPLIER, '| Gas limit:', gasLimit);

        await this.tokenContract.methods
          .approve(util.ORCHESTRATOR_ContractAddress, amountWei)
          .send({ from: this.account, gas: gasLimit.toString() })
          .on("receipt", async () => {
            util.toast.fire({ icon: "success", title: "Approval successful!" });
            this.approveAmount = "";
            await this.loadBalances();
            loader.hide();
            this.isApproving = false;
          })
          .on("error", (error) => {
            console.error("Error approving:", error);
            loader.hide();
            this.isApproving = false;
            util.toast.fire({ icon: "error", title: "Error approving tokens" });
          });
      } catch (error) {
        console.error("Error approving:", error);
        loader.hide();
        this.isApproving = false;
        util.toast.fire({ icon: "error", title: "Error approving tokens" });
      }
    },
  },
};
</script>

<style scoped></style>
